import API_BASE_URL from '../config';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
    id: string;
    email: string;
    name: string;
    phone?: string;
    profilePhoto?: string;
    roomNumber?: string;
    hotelName?: string;
    dietaryInfo?: string;
    location?: string;
    giftCode?: string;
    tokenNumber?: string;
    relationship?: string;
    familySide?: 'groom' | 'bride';
    role: 'superadmin' | 'admin' | 'guest' | 'photographer' | 'agency';
    status?: string;
    weddingPlan?: string;
    weddingCoupleName?: string;
    weddingDate?: string;
    weddingVenue?: string;
}

interface AuthContextType {
    user: User | null;
    setUser: (user: User | null) => void;
    accessToken: string | null;
    isAuthenticated: boolean;
    login: (username: string, password: string, role: string) => Promise<void>;
    logout: () => void;
    loading: boolean;
    isProfileModalOpen: boolean;
    openProfileModal: () => void;
    closeProfileModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);

    const openProfileModal = () => setIsProfileModalOpen(true);
    const closeProfileModal = () => setIsProfileModalOpen(false);
    // Sessions recovery and plan synchronization
    useEffect(() => {
        const initAuth = async () => {
            const storedUser = localStorage.getItem('user');
            const storedToken = localStorage.getItem('accessToken');

            if (storedUser && storedToken) {
                const parsedUser = JSON.parse(storedUser) as User;
                setUser(parsedUser);
                setAccessToken(storedToken);

                // Silently refresh Admin profile to capture any plan changes from SuperAdmin
                if (parsedUser.role === 'admin') {
                    try {
                        const res = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                            headers: { 'Authorization': `Bearer ${storedToken}` }
                        });
                        const data = await res.json();
                        if (res.ok && data.admin) {
                            const syncedUser = { 
                                ...parsedUser, 
                                weddingPlan: data.admin.weddingPlan,
                                status: data.admin.status 
                            };
                            setUser(syncedUser);
                            localStorage.setItem('user', JSON.stringify(syncedUser));
                        }
                    } catch (e) {
                        console.warn('Plan synchronization skipped');
                    }
                }
            }
            setLoading(false);
        };
        initAuth();
    }, []);

    const login = async (username: string, password: string, role: string) => {
        try {
            const endpoint =
                role === 'superadmin'
                    ? `${API_BASE_URL}/api/superadmin/login`
                    : role === 'agency'
                        ? `${API_BASE_URL}/api/agency/login`
                        : role === 'admin'
                            ? `${API_BASE_URL}/api/admin/login`
                            : role === 'photographer'
                                ? `${API_BASE_URL}/api/photographer/login`
                                : `${API_BASE_URL}/api/guests/login`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });

            const data = await response.json();
            console.log('[AuthContext] Login response data:', JSON.stringify(data));

            if (!response.ok) {
                throw new Error(data.message || 'Login failed');
            }

            let userData: User;
            if (role === 'superadmin') {
                userData = {
                    id: data.superAdmin.id,
                    email: data.superAdmin.email,
                    name: data.superAdmin.name,
                    role: 'superadmin'
                };
            } else if (role === 'agency') {
                userData = {
                    id: data.agency.id,
                    email: data.agency.email,
                    name: data.agency.name,
                    profilePhoto: data.agency.profilePhoto,
                    status: data.agency.status,
                    role: 'agency'
                };
            } else if (role === 'admin') {
                userData = {
                    id: data.admin.id,
                    email: data.admin.email,
                    name: data.admin.name,
                    phone: data.admin.phone,
                    profilePhoto: data.admin.profilePhoto,
                    status: data.admin.status,
                    weddingPlan: data.admin.weddingPlan,
                    weddingCoupleName: data.admin.weddingCoupleName,
                    weddingDate: data.admin.weddingDate,
                    weddingVenue: data.admin.weddingVenue,
                    role: 'admin'
                };
            } else if (role === 'photographer') {
                userData = {
                    id: data.photographer.id,
                    email: data.photographer.email,
                    name: data.photographer.name,
                    phone: data.photographer.phone,
                    profilePhoto: data.photographer.profilePhoto,
                    role: 'photographer'
                };
            } else {
                userData = {
                    id: data.guest.id,
                    email: data.guest.email,
                    name: data.guest.name,
                    phone: data.guest.phone,
                    profilePhoto: data.guest.profilePhoto,
                    roomNumber: data.guest.roomNumber,
                    hotelName: data.guest.hotelName,
                    dietaryInfo: data.guest.dietaryInfo,
                    location: data.guest.location,
                    giftCode: data.guest.giftCode,
                    tokenNumber: data.guest.tokenNumber,
                    familySide: data.guest.familySide,
                    role: 'guest'
                };
            }

            setUser(userData);
            setAccessToken(data.accessToken);

            // Store in localStorage
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            // Sync shortlist if exists
            const savedShortlist = localStorage.getItem('shadigram_shortlist');
            if (savedShortlist && (role === 'admin' || role === 'agency')) {
                const vendorIds = JSON.parse(savedShortlist);
                if (vendorIds.length > 0) {
                    try {
                        await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/sync`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${data.accessToken}`
                            },
                            body: JSON.stringify({ vendorIds })
                        });
                        // Clear shortlist after successful sync
                        localStorage.removeItem('shadigram_shortlist');
                    } catch (syncError) {
                        console.error('Failed to sync shortlist:', syncError);
                    }
                }
            }
        } catch (error: any) {
            throw error;
        }
    };

    const logout = () => {
        setUser(null);
        setAccessToken(null);
        setIsProfileModalOpen(false);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                accessToken,
                isAuthenticated: !!user,
                login,
                logout,
                loading,
                isProfileModalOpen,
                openProfileModal,
                closeProfileModal,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
